import { Subject } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { ClientLoggingService } from './client-logging.service';
import { ArchitecturalPlanPage } from './models/architectural-plan-page.model';
import { PlanReviewerService } from './plan-reviewer.service';
import { PdfWorkerService } from './pdf-worker.service';

export interface ParsedPdfCacheEntry {
  pdf: any;
  page: any;
}

export interface ParsedPageOptions {
  retain?: boolean;
}

/**
 * Singleton service that manages the pdf.js library reference and the LRU
 * parsed-PDF cache (Issue #728). Shared across all PdfRenderService instances
 * so component-scoped renderers avoid re-parsing documents that have already
 * been loaded.
 */
@Injectable({ providedIn: 'root' })
export class PdfCacheService {
  readonly textCacheInvalidated$ = new Subject<void>();

  private planReviewerService = inject(PlanReviewerService);
  private pdfWorkerService = inject(PdfWorkerService);

  private pdfjsLib: any;
  private parsedPdfCache = new Map<string, ParsedPdfCacheEntry>();
  private inFlightParsedPages = new Map<string, Promise<ParsedPdfCacheEntry | null>>();
  /** Entries currently being used by a renderer or text extractor. */
  private retainedEntries = new Map<string, number>();
  /** Evicted/invalidated entries waiting for their last consumer to finish. */
  private deferredDestroy = new Map<string, ParsedPdfCacheEntry[]>();
  static readonly MAX_PARSED_CACHE_SIZE = 10;

  constructor() {
    this.pdfjsLib = this.pdfWorkerService.getPdfjsLib();
  }

  getPdfjsLib(): any {
    return this.pdfjsLib;
  }

  /**
   * Returns a parsed pdf.js page for the given architectural plan page.
   * Uses the L2 cache to avoid expensive getDocument() re-parsing.
   */
  async getParsedPage(
    page: ArchitecturalPlanPage,
    options: ParsedPageOptions = {}
  ): Promise<ParsedPdfCacheEntry | null> {
    const cacheKey = PdfCacheService.buildCacheKey(page);
    const retained = options.retain === true;
    if (retained) this.retainKey(cacheKey);

    const cached = this.parsedPdfCache.get(cacheKey);
    if (cached) {
      ClientLoggingService.log('INFO', `[PdfCacheService] L2 cache hit for ${cacheKey}`);
      this.refreshCacheEntry(cacheKey);
      return cached;
    }

    const inFlight = this.inFlightParsedPages.get(cacheKey);
    if (inFlight) {
      ClientLoggingService.log('INFO', `[PdfCacheService] L2 in-flight hit for ${cacheKey}`);
      try {
        const entry = await inFlight;
        if (!entry && retained) this.releaseKey(cacheKey);
        return entry;
      } catch (error) {
        if (retained) this.releaseKey(cacheKey);
        throw error;
      }
    }

    ClientLoggingService.log('INFO', `[PdfCacheService] L2 cache miss for ${cacheKey}`);
    const loading = this.loadParsedPage(page);
    this.inFlightParsedPages.set(cacheKey, loading);
    try {
      const entry = await loading;
      // Invalidation removes the in-flight marker. Do not let an older parse
      // repopulate the cache after a page rotation or explicit refresh.
      if (entry && this.inFlightParsedPages.get(cacheKey) === loading) {
        this.addToParsedCache(cacheKey, entry);
      }
      if (!entry && retained) this.releaseKey(cacheKey);
      return entry;
    } catch (error) {
      if (retained) this.releaseKey(cacheKey);
      throw error;
    } finally {
      if (this.inFlightParsedPages.get(cacheKey) === loading) {
        this.inFlightParsedPages.delete(cacheKey);
      }
    }
  }

  static buildCacheKey(page: ArchitecturalPlanPage): string {
    const planId = page.getArchitecturalPlanId();
    const pageFolder = page.getPageFolderPath() || '';
    const baseKey = pageFolder ? `path-${pageFolder}` : page.getPageId() || `page-${page.getPageNumber()}`;
    return `${planId}:${baseKey}`;
  }

  retainParsedPage(page: ArchitecturalPlanPage): void {
    this.retainKey(PdfCacheService.buildCacheKey(page));
  }

  releaseParsedPage(page: ArchitecturalPlanPage): void {
    const key = PdfCacheService.buildCacheKey(page);
    this.releaseKey(key);
  }

  private retainKey(key: string): void {
    this.retainedEntries.set(key, (this.retainedEntries.get(key) ?? 0) + 1);
  }

  private releaseKey(key: string): void {
    const count = this.retainedEntries.get(key) ?? 0;
    if (count <= 1) this.retainedEntries.delete(key);
    else this.retainedEntries.set(key, count - 1);

    if (!this.retainedEntries.has(key)) {
      const deferred = this.deferredDestroy.get(key);
      if (deferred) {
        this.deferredDestroy.delete(key);
        deferred.forEach(entry => this.safeDestroyPdf(entry.pdf));
      }
    }
  }

  invalidatePageCache(page: ArchitecturalPlanPage): void {
    this.textCacheInvalidated$.next();
    const cacheKey = PdfCacheService.buildCacheKey(page);
    this.inFlightParsedPages.delete(cacheKey);
    const entry = this.parsedPdfCache.get(cacheKey);
    if (entry) {
      this.parsedPdfCache.delete(cacheKey);
      this.deferOrDestroy(cacheKey, entry);
      ClientLoggingService.log('INFO', `[PdfCacheService] L2 cache invalidated for ${cacheKey}`);
    }
  }

  clearParsedPdfCache(): void {
    this.textCacheInvalidated$.next();
    this.inFlightParsedPages.clear();
    for (const [key, entry] of this.parsedPdfCache) {
      this.deferOrDestroy(key, entry);
    }
    this.parsedPdfCache.clear();
    ClientLoggingService.log('INFO', '[PdfCacheService] L2 parsed PDF cache cleared');
  }

  getParsedPdfCacheSize(): number {
    return this.parsedPdfCache.size;
  }

  private addToParsedCache(key: string, entry: ParsedPdfCacheEntry): void {
    const isNewKey = !this.parsedPdfCache.has(key);
    if (isNewKey && this.parsedPdfCache.size >= PdfCacheService.MAX_PARSED_CACHE_SIZE) {
      const oldestKey = [...this.parsedPdfCache.keys()].find(key => !this.retainedEntries.has(key));
      if (oldestKey) {
        const evicted = this.parsedPdfCache.get(oldestKey);
        this.parsedPdfCache.delete(oldestKey);
        ClientLoggingService.log(
          'INFO',
          `[PdfCacheService] L2 cache evicted ${oldestKey} (size was ${PdfCacheService.MAX_PARSED_CACHE_SIZE})`
        );
        if (evicted) this.deferOrDestroy(oldestKey, evicted);
      }
    }
    this.parsedPdfCache.set(key, entry);
    ClientLoggingService.log('INFO', `[PdfCacheService] L2 cache stored ${key} (size: ${this.parsedPdfCache.size})`);
  }

  private async loadParsedPage(page: ArchitecturalPlanPage): Promise<ParsedPdfCacheEntry | null> {
    const pdfContent = await this.planReviewerService.getPagePdf(page);
    if (!pdfContent) return null;

    try {
      // PDF.js may transfer its input ArrayBuffer to the worker. Parse an
      // owned copy so the PlanReviewerService L1 byte cache remains reusable.
      const loadingTask = this.pdfjsLib.getDocument({ data: pdfContent.slice() });
      const pdf = await loadingTask.promise;
      const pdfPage = await pdf.getPage(1);
      return { pdf, page: pdfPage };
    } catch (error) {
      ClientLoggingService.log('ERROR', '[PdfCacheService] Error parsing PDF:', error);
      throw error;
    }
  }

  private refreshCacheEntry(key: string): void {
    const entry = this.parsedPdfCache.get(key);
    if (entry) {
      this.parsedPdfCache.delete(key);
      this.parsedPdfCache.set(key, entry);
    }
  }

  private deferOrDestroy(key: string, entry: ParsedPdfCacheEntry): void {
    if (this.retainedEntries.has(key)) {
      const deferred = this.deferredDestroy.get(key) ?? [];
      deferred.push(entry);
      this.deferredDestroy.set(key, deferred);
    } else this.safeDestroyPdf(entry.pdf);
  }

  private safeDestroyPdf(pdf: any): void {
    try {
      const result = pdf?.destroy?.();
      if (result && typeof result.catch === 'function') {
        result.catch(() => {});
      }
    } catch {
      // pdf.js destroy can throw synchronously if already cleaned up
    }
  }
}
